import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useContent } from '../hooks/useContent';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

const PALETTES = [
  { color: '#FC2120', bg: '#fff0f0' },
  { color: '#27ae60', bg: '#f0fff4' },
  { color: '#2c3e50', bg: '#f5f5f5' },
  { color: '#8e44ad', bg: '#fdf0ff' },
  { color: '#0077b6', bg: '#f0f8ff' },
  { color: '#16a085', bg: '#f0fffc' },
  { color: '#e67e22', bg: '#fff8f0' },
  { color: '#2980b9', bg: '#f0f8ff' },
];
const HEIGHTS = [176, 144, 160, 152, 168];

const FALLBACK = [
  { initials: 'DM' }, { initials: 'SN' }, { initials: 'AT' }, { initials: 'MK' },
  { initials: 'RS' }, { initials: 'VG' }, { initials: 'SP' }, { initials: 'PR' },
  { initials: 'PV' }, { initials: 'MM' }, { initials: 'NJ' }, { initials: 'AS' },
  { initials: 'AK' }, { initials: 'KR' }, { initials: 'RB' }, { initials: 'DS' },
];

function AlumniTile({ item, idx }) {
  const p = PALETTES[idx % PALETTES.length];
  const h = HEIGHTS[idx % HEIGHTS.length];
  const photo = resolveImg(item.photo);
  return (
    <div className="rounded-2xl overflow-hidden shadow-sm flex-shrink-0">
      {photo ? (
        <div style={{ height: h }} className="relative">
          <img src={photo} alt={item.name || item.initials} className="w-full h-full object-cover" />
          {item.name && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2">
              <div className="text-white text-xs font-bold leading-tight line-clamp-1">{item.name}</div>
              {item.uni && <div className="text-white/80 text-[10px] leading-tight line-clamp-1">{item.uni}</div>}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center" style={{ height: h, background: p.bg }}>
          <span className="text-4xl font-bold" style={{ color: p.color, opacity: 0.35 }}>{item.initials}</span>
        </div>
      )}
    </div>
  );
}

function Column({ items, startIdx, animClass }) {
  const doubled = [...items, ...items];
  return (
    <div className="flex-1 overflow-hidden rounded-2xl">
      <div className={`${animClass} flex flex-col gap-3`}>
        {doubled.map((d, i) => (
          <AlumniTile key={i} item={d} idx={startIdx + i} />
        ))}
      </div>
    </div>
  );
}

export default function Alumni() {
  const alumni = useContent('alumni', FALLBACK) || FALLBACK;
  const list = (alumni && alumni.length) ? alumni : FALLBACK;
  const chunk = Math.ceil(list.length / 4);
  const col1 = list.slice(0, chunk);
  const col2 = list.slice(chunk, chunk * 2);
  const col3 = list.slice(chunk * 2, chunk * 3);
  const col4 = list.slice(chunk * 3);

  return (
    <section id="alumni" className="py-20 md:py-24 bg-[#eef2ff] overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10">
          <div className="lg:w-[38%] flex-shrink-0 text-center lg:text-left">
            <p className="text-sm font-bold text-secondary uppercase tracking-widest mb-3">Our Alumni Impact</p>
            <h2 className="font-serif text-5xl md:text-[3.5rem] font-bold text-primary leading-tight mb-4">
              1,200+<br />
              <span className="text-brand">Doctors</span><br />
              Made a Choice
            </h2>
            <p className="text-base text-muted-foreground mb-8">
              That Opened the Right Doors.<br />
              Russian Education graduates are now proudly serving at India&apos;s finest hospitals.
            </p>
            <Link to="/alumni">
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 h-12 font-semibold shadow-lg btn-shift">
                Meet Our Alumni <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="lg:w-[62%] w-full flex gap-3" style={{ height: '480px' }}>
            <Column items={col1.length ? col1 : FALLBACK.slice(0, 4)} startIdx={0} animClass="col-scroll-up" />
            <Column items={col2.length ? col2 : FALLBACK.slice(4, 8)} startIdx={4} animClass="col-scroll-down" />
            <Column items={col3.length ? col3 : FALLBACK.slice(8, 12)} startIdx={8} animClass="col-scroll-up-slow" />
            <Column items={col4.length ? col4 : FALLBACK.slice(12, 16)} startIdx={12} animClass="col-scroll-down-slow" />
          </div>
        </div>
      </div>
    </section>
  );
}
